import '../../style/chart/MapWrapper.scss';

// react
import React, {useEffect, useRef, useState} from 'react';

// openlayers
import Map from 'ol/Map'
import OSM from 'ol/source/OSM.js';
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import {transform} from 'ol/proj'
import {Feature} from "ol";
import {LineString, Point, Polygon} from 'ol/geom';
import CircleStyle from 'ol/style/Circle';
import RegularShape from 'ol/style/RegularShape';
import {Fill, Stroke, Style, Text} from 'ol/style';
import proj4 from 'proj4';
import * as turf from "@turf/turf";
import {ScaleLine} from "ol/control";
import PointerInteraction from 'ol/interaction/Pointer';

type Props = {
    boreholes: any[],
    onSelectLineChanged: (selectLine: any[], selectedBoreholes: any[]) => void,
    offset?: number,
    selectLine?: any[],
    capturedBoreholeIds?: Set<string>,
    pickStartPointMode?: boolean,
    onStartPointPicked?: (coord: [number, number]) => void,
    onLineDragged?: (newStartCoord: [number, number]) => void,
    onLineAngleChanged?: (newAngle: number) => void,
    onLineLengthChanged?: (newLength: number) => void,
    zoomToLineTrigger?: number,
}

function MapWrapper(props: Props) {

    let initMap = true;

    const [map, setMap] = useState<any>();
    const mapElement: any = useRef();
    const mapRef: any = useRef();
    mapRef.current = map;

    // Persistent vector sources – created once, never replaced
    const selectLineSourceRef = useRef(new VectorSource());
    const selectArrowSourceRef = useRef(new VectorSource());
    const selectAreaSourceRef = useRef(new VectorSource());
    const selectedBhSourceRef = useRef(new VectorSource());
    const pointerInteractionRef = useRef<any>(null);

    // Drag session state for custom pointer interaction
    const dragModeRef = useRef<'translate' | 'rotate' | 'resize' | null>(null);
    const dragOffsetRef = useRef<[number, number]>([0, 0]);
    const dragOriginalLineRef = useRef<{ start: [number, number], end: [number, number] } | null>(null);
    const isDraggingRef = useRef(false);
    // rAF throttling – at most one drag callback emission per frame
    const dragRafRef = useRef<number | null>(null);
    const pendingDragEmitRef = useRef<(() => void) | null>(null);

    // Keep latest prop callbacks in refs so OL event handlers never go stale
    const pickStartPointModeRef = useRef(props.pickStartPointMode);
    pickStartPointModeRef.current = props.pickStartPointMode;
    const onStartPointPickedRef = useRef(props.onStartPointPicked);
    onStartPointPickedRef.current = props.onStartPointPicked;
    const boreholesRef = useRef(props.boreholes);
    boreholesRef.current = props.boreholes;
    const onLineDraggedRef = useRef(props.onLineDragged);
    onLineDraggedRef.current = props.onLineDragged;
    const onLineAngleChangedRef = useRef(props.onLineAngleChanged);
    onLineAngleChangedRef.current = props.onLineAngleChanged;
    const onLineLengthChangedRef = useRef(props.onLineLengthChanged);
    onLineLengthChangedRef.current = props.onLineLengthChanged;
    const onSelectLineChangedRef = useRef(props.onSelectLineChanged);
    onSelectLineChangedRef.current = props.onSelectLineChanged;

    // ── Map initialisation ──────────────────────────────────────────────────
    useEffect(() => {
        if (initMap) {
            initMap = false;
            if (mapElement.current && !mapRef.current) {

                const selectLineLayer = new VectorLayer({
                    // @ts-ignore
                    name: 'SelectLine',
                    source: selectLineSourceRef.current,
                    style: () => [new Style({
                        stroke: new Stroke({color: [0, 0, 255, 0.7], width: 3})
                    })]
                });

                const selectArrowLayer = new VectorLayer({
                    // @ts-ignore
                    name: 'SelectArrow',
                    source: selectArrowSourceRef.current,
                    zIndex: 10,
                    style: (feature) => {
                        const kind = feature.get('kind');
                        const rotation = feature.get('rotation') ?? 0;
                        if (kind === 'arrow') {
                            return new Style({
                                image: new RegularShape({
                                    points: 3,
                                    radius: 8,
                                    fill: new Fill({color: '#ff3300'}),
                                    stroke: new Stroke({color: '#ffffff', width: 1.5}),
                                    rotation: rotation,
                                    rotateWithView: true,
                                }),
                            });
                        }
                        if (kind === 'rotate-handle') {
                            return new Style({
                                image: new CircleStyle({
                                    radius: 4,
                                    fill: new Fill({color: '#ffcc00'}),
                                    stroke: new Stroke({color: '#ffffff', width: 1.5}),
                                }),
                            });
                        }
                        return new Style({});
                    },
                });

                const selectAreaLayer = new VectorLayer({
                    // @ts-ignore
                    name: 'SelectArea',
                    source: selectAreaSourceRef.current,
                    style: () => [new Style({
                        fill: new Fill({color: [0, 0, 255, 0.12]}),
                        stroke: new Stroke({color: [0, 0, 255, 0.3], width: 1.5})
                    })]
                });

                const selectedBhLayer = new VectorLayer({
                    // @ts-ignore
                    name: 'selected',
                    source: selectedBhSourceRef.current,
                    style: (feature) => new Style({
                        image: new CircleStyle({
                            radius: 7,
                            stroke: new Stroke({color: '#fff'}),
                            fill: new Fill({color: '#aa0000'}),
                        }),
                        text: getFeatureText(feature)
                    })
                });

                const initialMap = new Map({
                    target: mapElement.current,
                    layers: [
                        new TileLayer({source: new OSM()}),
                        new VectorLayer({source: new VectorSource()}), // placeholder features layer
                        selectAreaLayer,
                        selectLineLayer,
                        selectArrowLayer,
                        selectedBhLayer,
                    ],
                    view: new View({
                        projection: 'EPSG:3857',
                        center: [0, 0],
                        maxZoom: 18
                    }),
                    controls: [new ScaleLine()]
                });

                // Custom pointer interaction that translates / rotates / resizes
                // the selection line depending on where the drag starts.
                //
                // Perf: drag emissions are coalesced via requestAnimationFrame so
                // at most one state update per frame is fired. `onSelectLineChanged`
                // (which triggers expensive API calls in the parent) is skipped
                // while dragging and emitted once after drag end.
                const pointer = new PointerInteraction({
                    handleDownEvent: (evt: any) => {
                        if (pickStartPointModeRef.current) return false;
                        const mode = getLineDragMode(initialMap, evt.pixel);
                        if (!mode) return false;
                        const line = readLineFromSource();
                        if (!line) return false;
                        dragModeRef.current = mode;
                        dragOriginalLineRef.current = line;
                        dragOffsetRef.current = [
                            evt.coordinate[0] - line.start[0],
                            evt.coordinate[1] - line.start[1],
                        ];
                        isDraggingRef.current = true;
                        return true;
                    },
                    handleDragEvent: (evt: any) => {
                        const mode = dragModeRef.current;
                        const orig = dragOriginalLineRef.current;
                        if (!mode || !orig) return;
                        const cursor = evt.coordinate;

                        // Build the latest emit closure — overwrites any prior
                        // pending emit so we always use the newest cursor when
                        // the rAF fires.
                        pendingDragEmitRef.current = () => {
                            if (mode === 'translate') {
                                const newStartMap = [
                                    cursor[0] - dragOffsetRef.current[0],
                                    cursor[1] - dragOffsetRef.current[1],
                                ];
                                const newStart4326 = transform(newStartMap, 'EPSG:3857', 'EPSG:4326') as [number, number];
                                onLineDraggedRef.current?.(newStart4326);
                            } else if (mode === 'rotate') {
                                const start4326 = transform(orig.start, 'EPSG:3857', 'EPSG:4326');
                                const cursor4326 = transform(cursor, 'EPSG:3857', 'EPSG:4326');
                                const bearing = turf.bearing(
                                    turf.point(start4326 as [number, number]),
                                    turf.point(cursor4326 as [number, number]),
                                );
                                const newAngle = ((bearing % 360) + 360) % 360;
                                onLineAngleChangedRef.current?.(newAngle);
                            } else if (mode === 'resize') {
                                const start4326 = transform(orig.start, 'EPSG:3857', 'EPSG:4326');
                                const cursor4326 = transform(cursor, 'EPSG:3857', 'EPSG:4326');
                                const distKm = turf.distance(
                                    turf.point(start4326 as [number, number]),
                                    turf.point(cursor4326 as [number, number]),
                                    {units: 'kilometers'},
                                );
                                const newLength = Math.max(10, Math.round(distKm * 1000));
                                onLineLengthChangedRef.current?.(newLength);
                            }
                        };

                        if (dragRafRef.current === null) {
                            dragRafRef.current = requestAnimationFrame(() => {
                                dragRafRef.current = null;
                                const emit = pendingDragEmitRef.current;
                                pendingDragEmitRef.current = null;
                                emit?.();
                            });
                        }
                    },
                    handleUpEvent: () => {
                        // Flush any pending drag emission synchronously so the
                        // final cursor position is not dropped on mouseup.
                        if (dragRafRef.current !== null) {
                            cancelAnimationFrame(dragRafRef.current);
                            dragRafRef.current = null;
                            const emit = pendingDragEmitRef.current;
                            pendingDragEmitRef.current = null;
                            emit?.();
                        }
                        dragModeRef.current = null;
                        dragOriginalLineRef.current = null;
                        // Defer so React/Redux + updateSelectLine can flush the
                        // final line/buffer state before we emit the (expensive)
                        // onSelectLineChanged callback exactly once.
                        requestAnimationFrame(() => {
                            isDraggingRef.current = false;
                            emitSelectLineSelectionState();
                        });
                        return false;
                    },
                });
                initialMap.addInteraction(pointer);
                pointerInteractionRef.current = pointer;

                initialMap.on('click', handleMapClick);
                initialMap.on('pointermove', (evt: any) => {
                    if (pickStartPointModeRef.current || dragModeRef.current) return;
                    const mode = getLineDragMode(initialMap, evt.pixel);
                    const target = mapElement.current;
                    if (!target) return;
                    if (mode === 'resize') target.style.cursor = 'ew-resize';
                    else if (mode === 'rotate') target.style.cursor = 'grab';
                    else if (mode === 'translate') target.style.cursor = 'move';
                    else target.style.cursor = '';
                });
                initialMap.on('change:size', (e: any) => {
                    const size = initialMap.getSize();
                    if (size) {
                        try {
                            initialMap.getView().fit(e.target.previousExtent_, {size});
                        } catch (_) {}
                    }
                });

                setMap(initialMap);
            }
        }
    }, [mapElement, mapRef]);

    // ── Enable / disable drag interaction when pick-mode changes ───────────
    useEffect(() => {
        if (pointerInteractionRef.current) {
            pointerInteractionRef.current.setActive(!props.pickStartPointMode);
        }
    }, [props.pickStartPointMode]);

    // ── Emit onSelectLineChanged from the current sources (no props closure)
    //    Called once after drag end so the parent can refresh expensive
    //    derived state (e.g. fetchDetails) — while avoiding emissions on
    //    every pointermove during the drag itself.
    const emitSelectLineSelectionState = () => {
        const feats = selectLineSourceRef.current.getFeatures();
        if (feats.length === 0) return;
        const geom = feats[0].getGeometry() as LineString | undefined;
        if (!geom) return;
        const coords = geom.getCoordinates();
        if (coords.length < 2) return;
        const c1 = transform([coords[0][0], coords[0][1]], 'EPSG:3857', 'EPSG:4326') as [number, number];
        const c2 = transform([coords[1][0], coords[1][1]], 'EPSG:3857', 'EPSG:4326') as [number, number];

        const selectedBhs: any[] = [];
        const areaFeats = selectAreaSourceRef.current.getFeatures();
        if (areaFeats.length > 0) {
            const areaGeom = areaFeats[0].getGeometry() as any;
            for (const item of boreholesRef.current) {
                if (!item.collar || item.collar.LONGITUDE == null || item.collar.LATITUDE == null) continue;
                const t = proj4('EPSG:4326', 'EPSG:3857', [item.collar.LONGITUDE, item.collar.LATITUDE]);
                if (areaGeom?.intersectsCoordinate(t)) selectedBhs.push(item);
            }
        }
        onSelectLineChangedRef.current?.([c1, c2], selectedBhs);
    };

    // ── Helpers: read current line + determine drag mode ───────────────────
    const readLineFromSource = (): { start: [number, number], end: [number, number] } | null => {
        const feats = selectLineSourceRef.current.getFeatures();
        if (feats.length === 0) return null;
        const geom = feats[0].getGeometry() as LineString | undefined;
        if (!geom || geom.getType() !== 'LineString') return null;
        const coords = geom.getCoordinates();
        if (coords.length < 2) return null;
        return {
            start: [coords[0][0], coords[0][1]],
            end: [coords[coords.length - 1][0], coords[coords.length - 1][1]],
        };
    };

    /**
     * Determine whether a given pixel is on (or very near) the selection
     * line — and if so, which drag mode it should trigger:
     *   - 'resize'    → within ~14 px of the end point  → change length
     *   - 'rotate'    → in the last third of the line   → change angle
     *   - 'translate' → anywhere else on the line       → move the whole line
     */
    const getLineDragMode = (
        olMap: any,
        pixel: number[],
    ): 'translate' | 'rotate' | 'resize' | null => {
        const line = readLineFromSource();
        if (!line || !olMap) return null;
        const startPx = olMap.getPixelFromCoordinate(line.start);
        const endPx = olMap.getPixelFromCoordinate(line.end);
        if (!startPx || !endPx) return null;
        const dx = endPx[0] - startPx[0];
        const dy = endPx[1] - startPx[1];
        const len2 = dx * dx + dy * dy;
        if (len2 === 0) return null;
        // parameter along line, 0 = start, 1 = end
        const t = ((pixel[0] - startPx[0]) * dx + (pixel[1] - startPx[1]) * dy) / len2;
        // perpendicular distance from cursor to line
        const projX = startPx[0] + t * dx;
        const projY = startPx[1] + t * dy;
        const perpDist = Math.hypot(pixel[0] - projX, pixel[1] - projY);
        // resize takes priority — activates near the end point regardless of perp dist
        const distToEnd = Math.hypot(pixel[0] - endPx[0], pixel[1] - endPx[1]);
        if (distToEnd < 14) return 'resize';
        if (perpDist > 10) return null;
        if (t < 0 || t > 1) return null;
        if (t >= 2 / 3) return 'rotate';
        return 'translate';
    };

    // ── Zoom to line on external trigger ───────────────────────────────────
    useEffect(() => {
        if (!map || !props.zoomToLineTrigger) return;
        // ensure OL has processed any size changes of the container
        map.updateSize();
        const extent = selectLineSourceRef.current.getExtent();
        // getExtent() returns [Infinity,Infinity,-Infinity,-Infinity] when empty
        if (isFinite(extent[0])) {
            map.getView().fit(extent, {padding: [40, 40, 40, 40], duration: 500});
        }
    }, [props.zoomToLineTrigger, map]);

    // ── Borehole markers layer ──────────────────────────────────────────────
    useEffect(() => {
        if (!props.boreholes || props.boreholes.length === 0 || !map) return;

        map.getLayers().getArray()
            .filter((layer: any) => layer.get('name') === 'boreholes')
            .forEach((layer: any) => map.removeLayer(layer));

        const features: any[] = [];
        for (const item of props.boreholes) {
            if (!item.collar || item.collar.LONGITUDE == null || item.collar.LATITUDE == null) continue;
            const coords = proj4('EPSG:4326', 'EPSG:3857', [item.collar.LONGITUDE, item.collar.LATITUDE]);
            features.push(new Feature({geometry: new Point(coords), name: item.hole_id}));
        }
        if (features.length === 0) return;

        const source = new VectorSource({features});
        const capturedIds = props.capturedBoreholeIds;
        const layer = new VectorLayer({
            // @ts-ignore
            name: 'boreholes',
            source,
            style: (feature) => {
                const isCaptured = capturedIds?.has(feature.get('name')) ?? false;
                return new Style({
                    image: new CircleStyle({
                        radius: isCaptured ? 9 : 7,
                        stroke: new Stroke({
                            color: isCaptured ? '#ff6600' : '#fff',
                            width: isCaptured ? 2 : 1,
                        }),
                        fill: new Fill({color: isCaptured ? '#ff6600' : '#3399CC'}),
                    }),
                    text: getFeatureText(feature)
                });
            },
        });

        map.addLayer(layer);
        // Only fit to all boreholes if no selection line is being zoomed to
        if (!props.selectLine || props.selectLine.length === 0) {
            map.getView().fit(source.getExtent(), {padding: [200, 200, 200, 200]});
        }
    }, [props.boreholes, map, props.capturedBoreholeIds]);

    // ── Re-compute selection line whenever inputs change ────────────────────
    useEffect(() => {
        if (map) updateSelectLine();
    }, [props.offset, props.selectLine?.[0]?.[0], props.selectLine?.[0]?.[1], props.selectLine?.[1]?.[0], props.selectLine?.[1]?.[1], map]);

    // ── Map click handler ───────────────────────────────────────────────────
    const handleMapClick = (event: any) => {
        if (!mapRef.current) return;

        if (pickStartPointModeRef.current) {
            // Pick-mode: single click sets start point
            const clickedCoord = mapRef.current.getCoordinateFromPixel(event.pixel);
            const coord = transform(clickedCoord, 'EPSG:3857', 'EPSG:4326') as [number, number];
            onStartPointPickedRef.current?.(coord);
        }
    };

    // ── Selection line + offset buffer ─────────────────────────────────────
    const updateSelectLine = () => {
        const {selectLine, offset} = props;

        if (!map || !selectLine || selectLine.length !== 2 || selectLine[0][0] === undefined || !offset || offset < 1) {
            selectLineSourceRef.current.clear();
            selectArrowSourceRef.current.clear();
            selectAreaSourceRef.current.clear();
            selectedBhSourceRef.current.clear();
            return;
        }

        const c1 = [selectLine[0][0], selectLine[0][1]];
        const c2 = [selectLine[1][0], selectLine[1][1]];
        const c1Map = proj4('EPSG:4326', 'EPSG:3857', c1);
        const c2Map = proj4('EPSG:4326', 'EPSG:3857', c2);

        const ls = turf.lineString([c1, c2]);
        const buff = turf.buffer(ls, offset / 1.6, {units: 'meters'});

        const buffCoords: any[] = [];
        for (const coord of (buff.geometry?.coordinates[0] ?? [])) {
            buffCoords.push(proj4('EPSG:4326', 'EPSG:3857', [coord[0], coord[1]]));
        }

        // Update line feature
        selectLineSourceRef.current.clear();
        selectLineSourceRef.current.addFeature(
            new Feature({geometry: new LineString([c1Map, c2Map])})
        );

        // Update direction arrow + rotate-handle marker
        selectArrowSourceRef.current.clear();
        const dxMap = c2Map[0] - c1Map[0];
        const dyMap = c2Map[1] - c1Map[1];
        // OL RegularShape rotation: clockwise radians from up (north). A triangle
        // with points:3 points up by default, so atan2(dx, dy) aligns the tip
        // with the line direction (start → end).
        const rotation = Math.atan2(dxMap, dyMap);
        const twoThirdMap = [c1Map[0] + dxMap * (2 / 3), c1Map[1] + dyMap * (2 / 3)];

        const endArrow = new Feature({geometry: new Point(c2Map)});
        endArrow.set('kind', 'arrow');
        endArrow.set('rotation', rotation);
        selectArrowSourceRef.current.addFeature(endArrow);

        // Visual marker where the "rotate" zone begins (last third of line)
        const rotateHandle = new Feature({geometry: new Point(twoThirdMap)});
        rotateHandle.set('kind', 'rotate-handle');
        selectArrowSourceRef.current.addFeature(rotateHandle);

        // Update area feature
        selectAreaSourceRef.current.clear();
        const areaFeature = new Feature({geometry: new Polygon([buffCoords])});
        selectAreaSourceRef.current.addFeature(areaFeature);

        // Find boreholes inside buffer
        const selectedBhs: any[] = [];
        const bhFeatures: any[] = [];
        for (const item of props.boreholes) {
            if (!item.collar || item.collar.LONGITUDE == null || item.collar.LATITUDE == null) continue;
            const transCoord = proj4('EPSG:4326', 'EPSG:3857', [item.collar.LONGITUDE, item.collar.LATITUDE]);
            if (areaFeature.getGeometry()?.intersectsCoordinate(transCoord)) {
                selectedBhs.push(item);
                bhFeatures.push(new Feature({geometry: new Point(transCoord), name: item.hole_id}));
            }
        }

        selectedBhSourceRef.current.clear();
        selectedBhSourceRef.current.addFeatures(bhFeatures);

        // Suppress the (expensive) parent callback while the user is actively
        // dragging the line — we emit it exactly once from handleUpEvent via
        // emitSelectLineSelectionState() once the drag has settled.
        if (!isDraggingRef.current) {
            props.onSelectLineChanged(selectLine, selectedBhs);
        }
    };

    const getFeatureText = (feature: any) => new Text({
        text: feature.get('name'),
        font: 'bold 15px Calibri,sans-serif',
        fill: new Fill({color: 'black'}),
        stroke: new Stroke({color: 'white', width: 2}),
        offsetY: -20
    });

    return (
        <div
            ref={mapElement}
            className="map-container"
            style={{cursor: props.pickStartPointMode ? 'crosshair' : undefined}}
        />
    );
}

export default MapWrapper;
